'use client';

import {
  correctDateTime,
  DateTimePicker,
} from '@/components/girl/request-date';
import { DurationPicker } from '@/components/girl/request-duration';
import {
  requestAppointmentAddDate,
  requestAppointmentDurationDescription,
  requestAppointmentDurationTitle,
  requestAppointmentRemoveDate,
  requestDetailsWhenDescription,
  requestDetailsWhenTitle,
  requestLocationAdditionalDescription,
  requestLocationAdditionalPlaceholder,
  requestLocationAdditionalTitle,
  wizardNext,
  wizardPrev,
} from '@bells/i18n';
import { AttendantSearchSingleResponse } from '@bells/schema/models/attendant';
import { AutosizeTextarea } from '@bells/ui/components/autosize-textarea';
import { Button } from '@bells/ui/components/button';
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@bells/ui/components/dialog';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@bells/ui/components/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { isEmpty, last } from 'lodash';
import { Trash } from 'lucide-react';
import { useEffect } from 'react';
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from 'react-hook-form';

import { useRequest } from '@/components/girl/request-context';
import {
  RequestFormSchema,
  type RequestForm,
} from '@bells/schema/models/request';
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipTrigger,
} from '@bells/ui/components/tooltip';
import { addDays } from 'date-fns';
import { IconPlus } from '@tabler/icons-react';

export const StepChooseDate = ({
  data,
  resolve,
  goBack,
}: {
  data: AttendantSearchSingleResponse;
  resolve?: () => void;
  goBack?: () => void;
}) => {
  const request = useRequest();

  const form = useForm<
    Pick<RequestForm, 'duration' | 'addressAdditional'> & {
      startDate: (string | Date)[];
    }
  >({
    resolver: zodResolver(
      RequestFormSchema.pick({
        duration: true,
        addressAdditional: true,
        startDate: true,
      })
    ),

    mode: 'all',
    defaultValues: {
      startDate: request.values.startDate,
      duration: request.values.duration,
      addressAdditional: request.values.addressAdditional,
    },
  });

  const durationWatch = form.watch('duration');

  useEffect(() => {
    request.setValues({ duration: durationWatch });
  }, [durationWatch]);

  const { fields, append, remove } = useFieldArray<any>({
    control: form.control,
    name: 'startDate',
  });

  const submit = form.handleSubmit((val) => {
    request.setValues(val);
    if (resolve) {
      resolve();
    }
  });

  return (
    <>
      <DialogHeader className='p-6 pb-0'>
        <DialogTitle>{requestDetailsWhenTitle()}</DialogTitle>
        <DialogDescription>{requestDetailsWhenDescription()}</DialogDescription>
      </DialogHeader>
      <div className='w-full flex-1 overflow-y-auto p-6'>
        <FormProvider {...form}>
          <form
            className='flex w-full flex-col gap-4 lg:gap-6'
            onSubmit={submit}
          >
            <div className='flex w-full flex-col space-y-2'>
              {fields.map((item, index) => (
                <Controller
                  key={item.id}
                  render={({ field }) => (
                    <FormItem className={'w-full'}>
                      <FormLabel>{requestDetailsWhenTitle()}</FormLabel>

                      <FormControl>
                        <div className='flex w-full gap-1'>
                          <DateTimePicker
                            onChange={field.onChange}
                            initDate={
                              typeof field.value === 'string'
                                ? new Date(field.value)
                                : field.value
                            }
                          />
                          {index > 0 && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  type='button'
                                  variant={'outline'}
                                  onClick={() => remove(index)}
                                >
                                  <Trash></Trash>
                                </Button>
                              </TooltipTrigger>
                              <TooltipPortal>
                                <TooltipContent>
                                  {requestAppointmentRemoveDate()}
                                </TooltipContent>
                              </TooltipPortal>
                            </Tooltip>
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                  name={`startDate.${index}`}
                  control={form.control}
                />
              ))}
            </div>
            {fields.length <= 2 && (
              <Button
                type='button'
                variant={'outline'}
                onClick={() => {
                  const values = form.getValues();
                  const lastElement = last(values.startDate);
                  const f = lastElement
                    ? addDays(correctDateTime(lastElement), 1)
                    : undefined;

                  append(f ?? correctDateTime(new Date()));
                }}
              >
                <IconPlus></IconPlus>
                {requestAppointmentAddDate()}
              </Button>
            )}

            <FormField
              control={form.control}
              name='duration'
              render={({ field }) => (
                <FormItem className={'w-full'}>
                  <FormLabel>{requestAppointmentDurationTitle()}</FormLabel>
                  <FormDescription>
                    {requestAppointmentDurationDescription()}
                  </FormDescription>
                  <FormControl>
                    <DurationPicker
                      onChange={field.onChange}
                      duration={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='addressAdditional'
              render={({ field }) => (
                <FormItem className={'w-full'}>
                  <FormLabel>{requestLocationAdditionalTitle()}</FormLabel>
                  <FormDescription>
                    {requestLocationAdditionalDescription()}
                  </FormDescription>
                  <FormControl>
                    <AutosizeTextarea
                      minHeight={100}
                      className=''
                      placeholder={requestLocationAdditionalPlaceholder()}
                      maxLength={512}
                      {...field}
                      value={field.value ?? ''}
                      onChange={(val) => {
                        field.onChange(
                          isEmpty(val.currentTarget.value)
                            ? null
                            : val.currentTarget.value
                        );
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </FormProvider>
      </div>

      <DialogFooter className='gap-2 p-6 pt-0'>
        <Button type='button' onClick={goBack} variant={'outline'}>
          {wizardPrev()}
        </Button>
        <Button
          type='submit'
          onClick={submit}
          disabled={!form.formState.isValid}
        >
          {wizardNext()}
        </Button>
      </DialogFooter>
    </>
  );
};
